/**
 * Task numbers: per project, shown as SF-T-42 (not #42, which is a #tag). The two letters are the
 * project's task prefix (T-25), so a task from one project can't be taken for another's. Branches
 * keep the plain task/T-42-… form, and T-42 on its own still means that project's task.
 */

/** 42 and "SF" → "SF-T-42"; no prefix → "T-42"; blank before the server has numbered it. */
export function formatTaskNumber(number: number | null | undefined, prefix?: string | null): string {
  if (!number) return '';
  return prefix ? `${prefix}-T-${number}` : `T-${number}`;
}

/** "SF-T-42", "T-42", "t42" or "42" → { prefix, number }; anything else → null. */
export function parseTaskRef(value: string): { prefix: string | null; number: number } | null {
  const match = /^\s*(?:([a-z]{2})-t-?|t-?)?(\d{1,9})\s*$/i.exec(value);
  if (!match) return null;
  const number = Number(match[2]);
  return number > 0 ? { prefix: match[1]?.toUpperCase() ?? null, number } : null;
}

/**
 * The number in a ref (parseTaskRef). Given the project's prefix, a ref naming another project's
 * prefix (JF-T-42 inside SF) is null, so it never finds this project's T-42 by mistake.
 */
export function parseTaskNumber(value: string, projectPrefix?: string | null): number | null {
  const ref = parseTaskRef(value);
  if (!ref) return null;
  if (ref.prefix && projectPrefix && ref.prefix !== projectPrefix.toUpperCase()) return null;
  return ref.number;
}

/** A project's task prefix: exactly two letters A–Z, unique across projects. */
export const TASK_PREFIX_RE = /^[A-Z]{2}$/;

/** " sf " → "SF"; anything that isn't two letters → null. */
export function normaliseTaskPrefix(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const prefix = raw.trim().toUpperCase();
  return TASK_PREFIX_RE.test(prefix) ? prefix : null;
}

/**
 * The prefixes worth offering for a slug, best first: the first letters of its first two words
 * (software-factory → SF), then its first letter with each later one (docs → DO, DC, DS), then
 * every pair AA…ZZ. The project_task_prefix migration backfills existing projects the same way.
 */
export function taskPrefixCandidates(slug: string): string[] {
  const words = slug
    .toUpperCase()
    .replace(/[^A-Z-]/g, '')
    .split(/-+/)
    .filter(Boolean);
  const letters = words.join('');
  const out: string[] = [];
  if (words.length >= 2) out.push(words[0][0] + words[1][0]);
  for (let i = 1; i < letters.length; i++) out.push(letters[0] + letters[i]);
  for (let i = 0; i < 26 * 26; i++) out.push(String.fromCharCode(65 + Math.floor(i / 26), 65 + (i % 26)));
  return [...new Set(out)];
}

/** The first candidate for the slug that no other project has; null when all 676 are taken. */
export function suggestTaskPrefix(slug: string, taken: ReadonlySet<string>): string | null {
  return taskPrefixCandidates(slug).find((p) => !taken.has(p)) ?? null;
}
